import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Snackbar, Alert,
} from '@mui/material';
import { openPagePDF } from '../../../../../helpers/utils/utils';
import {
  imprimirRelatorioMemoriaDeCalculoPropostaAcordo, enviarMemoriaDeCalculoPorEmail,
} from '../../../../../services/core/processos/processosTitulosService';

const MENSAGEM_ERRO = 'Não foi possível concluir a operação.';

function paraNumeroMonetario(valor) {
  const numero = Number(valor);
  return Number.isFinite(numero) ? Number(numero.toFixed(2)) : 0;
}

function montarJsonTitulos(jsonTitulos) {
  if (!jsonTitulos) {
    return '[]';
  }
  const titulos = typeof jsonTitulos === 'string' ? JSON.parse(jsonTitulos) : jsonTitulos;
  if (!Array.isArray(titulos)) {
    throw new Error('jsonTitulos não é uma lista válida.');
  }
  return JSON.stringify(titulos.map(titulo => ({
    idTitulo: Number(titulo?.idTitulo) || 0,
    valorOriginal: paraNumeroMonetario(titulo?.valorOriginal),
    valorSaldoPrincipal: paraNumeroMonetario(titulo?.valorSaldoPrincipal),
    valorProtesto: paraNumeroMonetario(titulo?.valorProtesto),
    valorMulta: paraNumeroMonetario(titulo?.valorMulta),
    valorJurosCliente: paraNumeroMonetario(titulo?.valorJurosCliente),
    valorJurosABE: paraNumeroMonetario(titulo?.valorJurosABE ?? titulo?.valorJurosAbe),
    valorHonorarios: paraNumeroMonetario(titulo?.valorHonorarios),
    valorDesconto: paraNumeroMonetario(titulo?.valorDesconto),
    valorTotal: paraNumeroMonetario(titulo?.valorTotal),
  })));
}

function montarPayload(payload) {
  if (!payload) {
    return null;
  }
  return {
    valorSaldo: paraNumeroMonetario(payload.valorSaldo),
    valorJurosAbe: paraNumeroMonetario(payload.valorJurosAbe ?? payload.valorJurosABE),
    valorJurosCliente: paraNumeroMonetario(payload.valorJurosCliente),
    valorHonorarios: paraNumeroMonetario(payload.valorHonorarios),
    valorProtesto: paraNumeroMonetario(payload.valorProtesto),
    valorMulta: paraNumeroMonetario(payload.valorMulta),
    valorDesconto: paraNumeroMonetario(payload.valorDesconto),
    valorTotal: paraNumeroMonetario(payload.valorTotal),
    valorJuros: paraNumeroMonetario(payload.valorJuros),
    valorSubTotal: paraNumeroMonetario(payload.valorSubTotal),
    jsonTitulos: montarJsonTitulos(payload.jsonTitulos),
    porcentagemTaxaCartao: paraNumeroMonetario(payload.porcentagemTaxaCartao),
    valorTaxaCartao: paraNumeroMonetario(payload.valorTaxaCartao),
  };
}

/**
 * Port de `DialogMemoriaDeCalculo.jsx` real (`Titulos/DialogMemoriaDeCalculo/`)
 * para MUI v5 — mesma normalização de payload (valores monetários com 2
 * casas, `jsonTitulos` renormalizado), trocando `Grid24`/`react-toastify`/
 * `loadingInspecaoProcessoController` pelos equivalentes já estabelecidos no
 * protótipo (`Box`/`Snackbar`+`Alert`/`useState` local).
 */
export default function DialogMemoriaDeCalculo({
  open, onClose, idProcesso, payloadProposta,
}) {
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState('');

  // "Latest ref" — este dialog vive dentro da árvore NovoAcordo, que sobrevive
  // à troca de idProcesso sem desmontar (BotaoNovoAcordo preserva "aberto"
  // por processo). Mesmo guard obrigatório desde a Etapa 4 para qualquer
  // função assíncrona neste subárvore.
  const idProcessoRef = useRef(idProcesso);
  idProcessoRef.current = idProcesso;

  // Mesmo motivo do reset em NovoAcordoPassoDois.jsx: o guard acima impede
  // dado do processo errado de aplicar, mas também significa que o `finally`
  // de uma chamada em voo não desliga `carregando` para o processo novo.
  useEffect(() => {
    setCarregando(false);
    setErro('');
  }, [idProcesso]);

  const imprimir = async () => {
    const alvo = idProcesso;
    try {
      setCarregando(true);
      const payload = montarPayload(payloadProposta);
      const response = await imprimirRelatorioMemoriaDeCalculoPropostaAcordo(payload);
      if (idProcessoRef.current !== alvo) {
        return;
      }
      if (response?.data?.size > 0) {
        openPagePDF(response.data);
      }
      onClose();
    } catch {
      if (idProcessoRef.current === alvo) {
        setErro(MENSAGEM_ERRO);
      }
    } finally {
      if (idProcessoRef.current === alvo) {
        setCarregando(false);
      }
    }
  };

  const enviarEmail = async () => {
    const alvo = idProcesso;
    try {
      setCarregando(true);
      const payload = montarPayload(payloadProposta);
      await enviarMemoriaDeCalculoPorEmail(alvo, payload);
      if (idProcessoRef.current === alvo) {
        onClose();
      }
    } catch {
      if (idProcessoRef.current === alvo) {
        setErro(MENSAGEM_ERRO);
      }
    } finally {
      if (idProcessoRef.current === alvo) {
        setCarregando(false);
      }
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth hideBackdrop>
        <DialogTitle>Memória de Cálculo</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Você pode imprimir o relatório de Memória de Cálculo ou enviá-lo por e-mail ao devedor. Qual ação deseja realizar?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={carregando}>Cancelar</Button>
          <Button onClick={imprimir} disabled={carregando}>Imprimir</Button>
          <Button onClick={enviarEmail} disabled={carregando}>Enviar e-mail</Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={Boolean(erro)}
        autoHideDuration={4000}
        onClose={() => setErro('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="error" variant="filled" onClose={() => setErro('')}>
          {erro}
        </Alert>
      </Snackbar>
    </>
  );
}

DialogMemoriaDeCalculo.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  idProcesso: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  payloadProposta: PropTypes.object,
};

DialogMemoriaDeCalculo.defaultProps = {
  open: false,
  idProcesso: null,
  payloadProposta: null,
};
